/**
 * highlightQuotes.js — Inline quote highlighting for note text.
 *
 * Clinicians sometimes include direct quotes inside their notes using
 * standard quotation marks ("...", or typographic \u201C...\u201D). Rather
 * than showing these quotes in a separate section, we highlight them inline
 * with a warm amber background — the same visual treatment previously used
 * for the standalone "Direct Quotes" list.
 *
 * Usage:
 *   import { renderNoteWithQuotes } from '../../utils/highlightQuotes';
 *   <span>{renderNoteWithQuotes(note.text)}</span>
 *
 * Returns a React fragment of text and <span> elements. Non-quoted portions
 * render as plain text; quoted portions get the amber highlight styling.
 */
import React from 'react';

/**
 * Regex that matches text enclosed in straight or typographic double quotes.
 * Captures the inner content (group 1) so we can wrap it in a highlight span
 * while preserving surrounding text.
 */
const ANY_DQUOTE = '"\u201C\u201D';
const QUOTE_RE = new RegExp(`([${ANY_DQUOTE}])([^${ANY_DQUOTE}]+?)([${ANY_DQUOTE}])`, 'g');

/**
 * Splits a note string into plain-text and highlighted-quote segments, then
 * returns a React fragment with the quotes wrapped in an amber-highlighted
 * span. If the text contains no quotes, returns the original string.
 *
 * @param {string} text — The full note text, potentially containing quoted
 *   substrings like "she always did this" or \u201Che said he struggles\u201D.
 * @returns {React.ReactNode} — Either the plain string (no quotes found) or a
 *   React fragment with highlighted spans for each quoted substring.
 */
export function renderNoteWithQuotes(text) {
  if (!text) return text;

  const parts = [];
  let lastIndex = 0;
  let match;

  QUOTE_RE.lastIndex = 0;
  while ((match = QUOTE_RE.exec(text)) !== null) {
    // Plain text before this quote
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    // The quoted portion — wrapped in an amber highlight span.
    // We preserve the original opening/closing quote characters so the
    // clinician sees exactly what they typed.
    const openQuote = match[1];
    const innerText = match[2];
    const closeQuote = match[3];
    parts.push(
      <span
        key={match.index}
        className="italic px-0.5 rounded"
        style={{ backgroundColor: 'rgba(251,191,36,0.22)', color: '#78350f' }}
      >
        {openQuote}{innerText}{closeQuote}
      </span>
    );

    lastIndex = match.index + match[0].length;
  }

  // If no quotes were found, return the original string unchanged.
  if (parts.length === 0) return text;

  // Trailing text after the last quote
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return <>{parts}</>;
}
